/** @format */

import React, { useEffect, useState } from "react";
import isSameDay from "date-fns/isSameDay";
import { useQueryAccounts } from "../../../../apis/swr/fetchers";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { ChartOptions } from 'chart.js';
import SignUpChartsView from "./SignUpCharts.view";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface Account {
  dateCreated?: string;
}

interface SeriesData {
  x: number;
  y: number;
}

const SignUpChartsContainer: React.FC = () => {
  const { accounts } = useQueryAccounts();
  const [data, setData] = useState<SeriesData[]>([]);

  useEffect(() => {
    const createDataSet = () => {
      const dateArray: SeriesData[] = [];
      // Create last 42 days (6 weeks)
      for (let i = 0; i < 42; i++) {
        let date = new Date();
        date.setDate(date.getDate() - i);
        const utcDate = Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes()
        );
        dateArray.push({ x: utcDate, y: 0 });
      }

      dateArray.forEach((date) => {
        accounts?.forEach((account) => {
          if (account.dateCreated) {
            if (isSameDay(new Date(account.dateCreated), new Date(date.x))) {
              date.y += 1;
            }
          }
        });
      });

      setData(dateArray);
    };

    createDataSet();
  }, [accounts]);

  const chartData = {
    datasets: [
      {
        label: 'Signups',
        data: data,
        borderColor: 'rgb(44,175,254, 1)',
        backgroundColor: 'rgb(44,175,254, 0.2)',
        tension: 0.4,
        pointHoverRadius: 10, // Increase hover radius
        pointHitRadius: 10,   // Increase hit radius
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Sign Up Counts',
        font: {
          size: 20,
          family: 'Helvetica, Arial, sans-serif',
        },
        color: 'rgb(51, 51, 51)',
      },
      tooltip: {
        position: 'nearest',
        backgroundColor: '#ffffff', // White background
        titleColor: '#000000', // Black title text
        bodyColor: '#000000', // Black body text
        titleAlign: 'center',
        bodyAlign: 'center',
        displayColors: false,
        borderColor: '#dddddd', // Border color for tooltip
        borderWidth: 1, // Border width for tooltip
        callbacks: {
          label: (context) => {
            return `Signups: ${context.parsed.y}`;
          },
          title: (tooltipItems) => {
            const date = new Date(tooltipItems[0].parsed.x ?? 0);
            return date.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'PPPP',
          displayFormats: {
            day: 'd MMM',
          },
        },
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          callback: function (value, index, values) {
            // Show only alternate dates
            if (index % 2 === 0) {
              const date = new Date(value as number);
              return new Intl.DateTimeFormat('en-US', {
                day: 'numeric',
                month: 'short',
              }).format(date);
            }
            return '';
          },
          maxRotation: 0, // Ensure the labels are displayed straight
          minRotation: 0,
        },
        grid: {
          display: true, // Show x-axis grid lines
          drawOnChartArea: false, // Only draw grid lines on ticks
          drawTicks: true,
          tickLength: 5, // Length of the small grid lines
        },
      },
      y: {
        title: {
          display: true,
          text: 'Count',
        },
        grid: {
          display: false, // Hide y-axis grid lines
        },
      },
    },
  };

  return (
    <SignUpChartsView chartData={chartData} options={options} />
  );
};

export default SignUpChartsContainer;
